import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useOffersApi } from '../../hooks/useOffersApi';
import { useSnackbar } from '../Stateless/SnackBarProvider';

type Params = {
  sortBy?: string;
  sortOrder?: string;
  page?: number;
  limit?: number;
  search?: string;
};

export const useAllOffers = (queryParams: Params) => {
  const { getAllOffers } = useOffersApi();
  const {
    data,
    isLoading: isFetching,
    refetch,
    isRefetching,
    error,
    isError,
  } = useQuery({
    queryKey: ['getOffers', queryParams],
    queryFn: () => {
      return getAllOffers(queryParams);
    },
    retry: 1,
    retryDelay: 1000,
  });

  const isLoading = isFetching || isRefetching;

  return {
    data,
    isLoading,
    refetch,
    error,
    isError,
  };
};

export const useCreateOffer = () => {
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();
  const { createOffer } = useOffersApi();
  return useMutation({
    mutationKey: ['CreateOffer'],
    mutationFn: (offerData: any) => {
      return createOffer(offerData);
    },
    onSuccess: (response: any) => {
      showMessage(
        response?.message || 'Offer created successfully',
        'success'
      );
      queryClient.invalidateQueries({
        queryKey: ['getOffers'],
        exact: false,
      });
    },
    onError: (error: any) => {
      showMessage(
        error.response?.data?.message || 'Failedz to create Offer',
        'error'
      );
    },
  });
};

export const useEditOffer = () => {
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();
  const { editOffer } = useOffersApi();

  return useMutation({
    mutationKey: ['EditOffer'],
    mutationFn: ({ offerId, data }: { offerId: string; data: any }) =>
      editOffer(offerId, data),
    onSuccess: (response: any) => {
      // Handle both response.data.message and response.message
      const message = response?.message || response?.data?.message || 'Offer Updated successfully';
      showMessage(message, 'success');
      queryClient.invalidateQueries({
        queryKey: ['getOffers'],
        exact: false,
      });
    },
    onError: (error: any) => {
      // Check if error has a message that indicates success (edge case)
      const errorMessage = error?.message || error?.response?.data?.message;
      if (errorMessage && errorMessage.toLowerCase().includes('success')) {
        showMessage(errorMessage, 'success');
        queryClient.invalidateQueries({
          queryKey: ['getOffers'],
          exact: false,
        });
      } else {
        showMessage(
          errorMessage || 'Failed to update the Offer',
          'error'
        );
      }
    },
  });
};

export const useDeleteOffer = () => {
  const queryClient = useQueryClient();
  const { deleteOffers } = useOffersApi();
  const { showMessage } = useSnackbar();

  return useMutation({
    mutationKey: ['DeleteOffer'],
    mutationFn: (offerIds: string[]) => {
      return deleteOffers(offerIds);
    },
    onSuccess: (response: any) => {
      showMessage(
        response?.message || 'Offer deleted successfully',
        'success'
      );
      queryClient.invalidateQueries({
        queryKey: ['getOffers'],
        exact: false,
      });
    },
    onError: (error: any) => {
      showMessage(
        error.response?.data?.message || 'Failed to delete Offer',
        'error'
      );
    },
  });
};

export const useToggleOfferStatus = () => {
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();
  const { toggleOfferStatus } = useOffersApi();

  return useMutation({
    mutationKey: ['ToggleOfferStatus'],
    mutationFn: ({ offerIds, status }: { offerIds: string[]; status: 'blocked' | 'unblocked' }) =>
      toggleOfferStatus(offerIds, status),
    onSuccess: (_response, variables) => {
      const action = variables.status === 'unblocked' ? 'unblocked' : 'blocked';
      showMessage(`Offer ${action} successfully`, 'success');
      queryClient.invalidateQueries({
        queryKey: ['getOffers'],
        exact: false,
      });
    },
    onError: (error: any) => {
      showMessage(
        error.response?.data?.message || 'Failed to toggle offer status',
        'error'
      );
    },
  });
};

export const useGenerateCouponCode = () => {
  const { generateCouponCode } = useOffersApi();
  const { showMessage } = useSnackbar();

  return useMutation({
    mutationKey: ['GenerateCouponCode'],
    mutationFn: () => generateCouponCode(),
    onSuccess: () => {
      // Success is handled by the component using the mutation result
    },
    onError: (error: any) => {
      showMessage(
        error.response?.data?.message || 'Failed to generate coupon code',
        'error'
      );
    },
  });
};

export const useAssignOffer = () => {
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();
  const { assignOffer } = useOffersApi();

  return useMutation({
    mutationKey: ['AssignOffer'],
    mutationFn: (assignData: any) => assignOffer(assignData),
    onSuccess: (response: any) => {
      showMessage(
        response?.message || 'Offers assigned successfully',
        'success'
      );
      queryClient.invalidateQueries({
        queryKey: ['getCustomersDetailsForOffers'],
        exact: false,
      });
    },
    onError: (error: any) => {
      showMessage(
        error.response?.data?.message || 'Failed to assign offers',
        'error'
      );
    },
  });
};

export const useGetAvailableOffersForAssignment = () => {
  const { getAvailableOffersForAssignment } = useOffersApi();
  const {
    data,
    isLoading: isFetching,
    refetch,
    isRefetching,
    error,
    isError,
  } = useQuery({
    queryKey: ['getAvailableOffersForAssignment'],
    queryFn: () => getAvailableOffersForAssignment(),
    retry: 1,
    retryDelay: 1000,
  });

  const isLoading = isFetching || isRefetching;

  return {
    data,
    isLoading,
    refetch,
    error,
    isError,
  };
};

export const useGetCustomerDetails = (userId: string | null) => {
  const { getCustomerDetails } = useOffersApi();
  const {
    data,
    isLoading: isFetching,
    refetch,
    isRefetching,
    error,
    isError,
  } = useQuery({
    queryKey: ['getCustomerDetails', userId],
    queryFn: () => getCustomerDetails(userId!),
    enabled: !!userId,
    retry: 1,
    retryDelay: 1000,
  });

  const isLoading = isFetching || isRefetching;

  return {
    data,
    isLoading,
    refetch,
    error,
    isError,
  };
};

export const useGetCustomerLoyaltyHistory = (userId: string | null, page: number = 1, limit: number = 10) => {
  const { getCustomerLoyaltyHistory } = useOffersApi();
  const {
    data,
    isLoading: isFetching,
    refetch,
    isRefetching,
    error,
    isError,
  } = useQuery({
    queryKey: ['getCustomerLoyaltyHistory', userId, page, limit],
    queryFn: () => getCustomerLoyaltyHistory(userId!, page, limit),
    enabled: !!userId,
    retry: 1,
    retryDelay: 1000,
  });

  const isLoading = isFetching || isRefetching;

  return {
    data,
    isLoading,
    refetch,
    error,
    isError,
  };
};

export const useGetCustomersDetailsForOffers = (queryParams: Params) => {
  const { getCustomersDetailsForOffers } = useOffersApi();
  const {
    data,
    isLoading: isFetching,
    refetch,
    isRefetching,
    error,
    isError,
  } = useQuery({
    queryKey: ['getCustomersDetailsForOffers', queryParams],
    queryFn: () => getCustomersDetailsForOffers(queryParams),
    retry: 1,
    retryDelay: 1000,
  });

  const isLoading = isFetching || isRefetching;

  return {
    data,
    isLoading,
    refetch,
    error,
    isError,
  };
};

export const useUpdateWallet = () => {
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();
  const { updateWallet } = useOffersApi();

  return useMutation({
    mutationKey: ['UpdateWallet'],
    mutationFn: (walletData: any) => updateWallet(walletData),
    onSuccess: (response: any) => {
      showMessage(
        response?.message || 'Wallet updated successfully',
        'success'
      );
      queryClient.invalidateQueries({
        queryKey: ['getCustomersDetailsForOffers'],
        exact: false,
      });
    },
    onError: (error: any) => {
      showMessage(
        error.response?.data?.message || 'Failed to update wallet',
        'error'
      );
    },
  });
};

